import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import api from "../../api";

const PARENT_PREFIX = process.env.REACT_APP_PARENT_PREFIX || "parent";

const pctColor = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "default";
  if (n >= 80) return "success";
  if (n >= 50) return "warning";
  return "error";
};

const fmtPct = (v) => Math.round(Number(v) || 0);

const ParentReadinessOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("parent/readiness-overview");
        if (res.data?.success && res.data?.data?.students) {
          setStudents(res.data.data.students);
        } else {
          setStudents([]);
          setError(res.data?.message || "Could not load readiness.");
        }
      } catch (err) {
        console.error(err);
        setStudents([]);
        setError(err.response?.data?.message || "Failed to load readiness overview.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (students.length === 1) {
      setSelectedStudentId(String(students[0].student_id));
    }
  }, [students]);

  const visibleStudents = useMemo(() => {
    if (!selectedStudentId) {
      return students;
    }
    return students.filter((s) => String(s.student_id) === selectedStudentId);
  }, [students, selectedStudentId]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Readiness
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Target schools selected for each child, with overall readiness (papers, mock exams, and
        assignments) and subject averages from assignments linked to that school.
      </Typography>

      {!loading && students.length > 0 && (
        <FormControl size="small" sx={{ width: { xs: "100%", sm: 260 }, minWidth: { sm: 260 }, maxWidth: "100%", mb: 3 }}>
          <InputLabel id="readiness-student-select-label">Student</InputLabel>
          <Select
            labelId="readiness-student-select-label"
            id="readiness-student-select"
            label="Student"
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value)}
          >
            {students.length > 1 && (
              <MenuItem value="">
                <em>All students</em>
              </MenuItem>
            )}
            {students.map((stu) => (
              <MenuItem key={stu.student_id} value={String(stu.student_id)}>
                {stu.student_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && students.length === 0 && (
        <Alert severity="info">No students found.</Alert>
      )}

      {!loading &&
        visibleStudents.map((stu) => (
          <Card key={stu.student_id} sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {stu.student_name}
              </Typography>
              {!stu.schools || stu.schools.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No target schools on file for this student.
                </Typography>
              ) : (
                stu.schools.map((sch, idx) => (
                  <Box key={`${stu.student_id}-${sch.target_school_id}`} sx={{ mt: idx ? 3 : 1 }}>
                    {idx > 0 && <Divider sx={{ mb: 2 }} />}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>
                        {sch.target_school_name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Chip
                          label={`Total ${Math.round(Number(sch.total_percentage) || 0)}%`}
                          color={pctColor(sch.total_percentage)}
                          variant="filled"
                          sx={{ fontWeight: 700 }}
                        />
                        <Button
                          size="small"
                          endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                          onClick={() =>
                            navigate(
                              `/${PARENT_PREFIX}/readiness/${stu.student_id}/${sch.target_school_id}`,
                              {
                                state: {
                                  studentName: stu.student_name,
                                  targetSchoolName: sch.target_school_name,
                                },
                              }
                            )
                          }
                        >
                          Weak areas
                        </Button>
                      </Box>
                    </Box>
                    {/* <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      Average % is from assignments only. Papers and mocks are counted when the exam category name
                      matches the subject (including parent categories).
                    </Typography> */}
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, overflow: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Subject</TableCell>
                            <TableCell align="center">Average % </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                              Papers ({fmtPct(sch.papers_percentage)}%)
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                              Mocks ({fmtPct(sch.mock_exams_percentage)}%)
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                              Assignments ({fmtPct(sch.assignments_percentage)}%)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(sch.subjects || []).map((sub) => (
                            <TableRow key={sub.id}>
                              <TableCell>{sub.name}</TableCell>
                              <TableCell align="center">
                                {sub.percentage != null ? (
                                  <Chip
                                    size="small"
                                    label={`${Math.round(Number(sub.percentage))}%`}
                                    color={pctColor(sub.percentage)}
                                    variant="outlined"
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">{sub.papers_count ?? 0}</TableCell>
                              <TableCell align="center">{sub.mocks_count ?? 0}</TableCell>
                              <TableCell align="center">{sub.assignments_count ?? sub.tests_count ?? 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        ))}
    </Box>
  );
};

export default ParentReadinessOverview;
